import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Divider, Flex, Tooltip } from "@chakra-ui/react";
import ToolButton from "./ToolButton";
import ShapePicker from "./ShapePicker";
import ColourPicker from "./ColourPicker";
import { ReactComponent as DeleteIcon } from "./icons/delete.svg";
import { PALETTES } from "../classroomColors";
import { TOOL_LIST, TOOLS } from "./boardTools";

const ROW = 36;
const GAP = 13;

/**
 * The board toolbar (Figma 205:349): a vertical pill of tools, a rule, then
 * clear-all and the current colour.
 *
 * It owns no board state of its own — the active tool, shape and colour are
 * passed in and changed through callbacks, so the same bar can drive a
 * session's canvas, a shared whiteboard or a preview without being rewritten.
 * Its only local state is which flyout is open, which nothing outside cares
 * about.
 */
const BoardToolbar = ({
  /** Which tools this bar offers. A board shows them all; a paper shows the
   *  few that mean something on a paper. Defaults to the whole set, so the
   *  board carries on unchanged. */
  tools = TOOL_LIST,
  tool,
  onToolChange,
  shape,
  onShapeChange,
  colour,
  onColourChange,
  /** The swatches offered. Strings are fine; they are given labels here. */
  colours,
  onClear,
  canClear = false,
  /** Step back one change. Wired to the usual shortcut when given. */
  onUndo,
  isDark = false,
  ...rest
}) => {
  const [openFlyout, setOpenFlyout] = useState(null);

  // Which way there is room, worked out where the bar actually is rather
  // than assumed: the same bar is docked right on a paper and could be
  // anywhere on a board.
  const barRef = useRef(null);
  const [flyoutSide, setFlyoutSide] = useState("left");
  useEffect(() => {
    if (!openFlyout || !barRef.current) return;
    const r = barRef.current.getBoundingClientRect();
    setFlyoutSide(r.left + r.width / 2 > window.innerWidth / 2 ? "left" : "right");
  }, [openFlyout]);
  const p = isDark ? PALETTES.dark : PALETTES.light;

  const selectTool = useCallback(
    (entry) => {
      onToolChange(entry.id);
      // A tool with a flyout toggles it; picking any other tool closes one.
      setOpenFlyout((prev) =>
        entry.flyout ? (prev === entry.flyout ? null : entry.flyout) : null
      );
    },
    [onToolChange]
  );

  // Single-key shortcuts, ignored while the user is typing into something.
  useEffect(() => {
    const onKey = (e) => {
      const el = e.target;
      // A field inside a note is part of the board, not a form: after
      // writing one note and pressing Tab you are typing in the next, and
      // undo there still means the board.
      const inItem = el && el.closest && el.closest("[data-board-item]");
      const typing =
        !inItem && el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

      // Undo is the one shortcut that carries a modifier, and the one people
      // reach for without thinking. Outside the board it is left alone,
      // where undo means the text field's own.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        // Something nearer the pen has already dealt with it — an answer box
        // with its own ink out, for instance.
        if (e.__standeUndoHandled) return;
        if (typing || !onUndo) return;
        e.preventDefault();
        onUndo();
        return;
      }

      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (typing) return;
      if (e.key === "Escape") {
        setOpenFlyout(null);
        onToolChange(TOOLS.SELECT);
        return;
      }
      const entry = tools.find((t) => t.shortcut === e.key.toLowerCase());
      if (entry) {
        e.preventDefault();
        selectTool(entry);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectTool, onToolChange, tools, onUndo]);

  const rowTop = (index) => index * (ROW + GAP);

  // A caller may hand plain colour strings; the picker wants something it can
  // label and key by.
  const palette = useMemo(
    () =>
      colours
        ? colours.map((c) =>
            typeof c === "string" ? { id: c, value: c, label: c } : c
          )
        : undefined,
    [colours]
  );

  return (
    <Box position="relative" ref={barRef} {...rest}>
      <Flex
        direction="column"
        align="center"
        gap={`${GAP}px`}
        px="7px"
        py="11px"
        borderRadius="200px"
        bg={p.surface}
        border="1px solid"
        borderColor={p.border}
        boxShadow="0 0 2.8px 0 rgba(0,0,0,0.18)"
        role="toolbar"
        aria-label="Board tools"
        aria-orientation="vertical"
      >
        {tools.map((entry) => (
          <ToolButton
            key={entry.id}
            icon={entry.icon}
            label={entry.label}
            shortcut={entry.shortcut}
            isActive={tool === entry.id}
            isDark={isDark}
            onClick={() => selectTool(entry)}
          />
        ))}

        <Divider borderColor={p.border} width="22px" my="-2px" />

        <Tooltip label="Clear the board" placement="left" hasArrow fontSize="11px">
          <Box
            as="button"
            type="button"
            aria-label="Clear the board"
            onClick={onClear}
            disabled={!canClear}
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxSize="36px"
            borderRadius="full"
            bg={p.surfaceSubtle}
            color={p.text}
            opacity={canClear ? 1 : 0.4}
            cursor={canClear ? "pointer" : "not-allowed"}
            transition="background 0.15s"
            _hover={canClear ? { bg: p.hover } : {}}
            _focusVisible={{ outline: "2px solid", outlineColor: p.text, outlineOffset: "2px" }}
          >
            {/* The asset points the way a "delete right" key does; on a
                toolbar it reads as a backspace, so it is mirrored. */}
            <DeleteIcon
              width="22px"
              height="22px"
              aria-hidden
              focusable="false"
              style={{ transform: "scaleX(-1)" }}
            />
          </Box>
        </Tooltip>

        <Tooltip label="Colour" placement="left" hasArrow fontSize="11px">
          <Box
            as="button"
            type="button"
            aria-label="Colour"
            aria-pressed={openFlyout === "colours"}
            onClick={() => setOpenFlyout((prev) => (prev === "colours" ? null : "colours"))}
            boxSize="26px"
            borderRadius="full"
            bg={colour}
            boxShadow={
              openFlyout === "colours" ? `0 0 0 2px ${p.surface}, 0 0 0 4px ${p.text}` : "none"
            }
            transition="box-shadow 0.15s"
            _focusVisible={{ outline: "2px solid", outlineColor: p.text, outlineOffset: "3px" }}
          />
        </Tooltip>
      </Flex>

      <ShapePicker
        isOpen={openFlyout === "shapes"}
        value={shape}
        onChange={(next) => {
          onShapeChange(next);
          onToolChange(TOOLS.SHAPE);
          setOpenFlyout(null);
        }}
        isDark={isDark}
        side={flyoutSide}
        top={rowTop(tools.findIndex((t) => t.id === TOOLS.SHAPE))}
      />

      <ColourPicker
        isOpen={openFlyout === "colours"}
        colours={palette}
        value={colour}
        onChange={(next) => {
          onColourChange(next);
          setOpenFlyout(null);
        }}
        isDark={isDark}
        side={flyoutSide}
        top={rowTop(tools.length) + 40}
      />
    </Box>
  );
};

export default BoardToolbar;
